import { Injectable, Pipe, PipeTransform, EventEmitter }     from '@angular/core';
import {SearchService} from './search';
import {RoutesService} from './routes';
import {OpenAPIService} from './openapi';
import {TrackerService} from './tracker';
import {Title, Meta} from '@angular/platform-browser';

declare let window: any;

const initializeNavigation = require('../../lib/initialize-navigation');

export interface MenuItem {
  title:string;
  path:string;
  icon?:string;
  operation?:string;
  tag?:string;
  contents?:string;
  summary?:string;
  hide:boolean;
  hidden:boolean;
  expand:boolean;
  expanded:boolean;
  children?: MenuItem[];
}

@Injectable()
export class MenuService {
  items:MenuItem[] = [];
  tags:string[] = [];
  currentChoice:any;
  choose:EventEmitter<any> = new EventEmitter();
  onlyAPICalls:boolean;
  unparsedNavigation:any;
  redirecting:string;
  currentNavigationSettings:any;
  subscriptions:any[] = [];

  constructor(private routes:RoutesService,
              private meta:Meta,
              private title:Title,
              private openapi:OpenAPIService,
              private tracker:TrackerService,
              private search:SearchService) {
    window.menuService = this;
    let paramSub = null;
    let triedRedirect = false;
    this.subscriptions.push(this.routes.currentRoute.subscribe(route => {
      if (!route) return this.clearNavigation();
      let nav = this.routes.getCurrentNavigation();
      this.setNavigation(nav);
      if (paramSub) paramSub.unsubscribe();
      paramSub = this.routes.router.routerState.root.children[0].params.subscribe(params => {
        let didRedirect = false;
        if (!triedRedirect) {
          didRedirect = this.maybeRedirectFromHash();
          triedRedirect = true;
        }
        if (!didRedirect) this.expandForParams(params);
      })
    }));
    this.subscriptions.push(this.choose.subscribe(choice => {
      this.setMeta(choice);
      if (choice) {
        this.tracker.track('menu', {title: choice.title, path: choice.path});
      }
    }));
    this.subscriptions.push(this.openapi.spec.subscribe(spec => {
      let nav = this.routes.getCurrentNavigation();
      this.setNavigation(nav);
      this.expandForParams(this.routes.getPathParams());
    }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  maybeRedirectFromHash() {
    if (!window.config.uiOptions.redirectHashURLs) return;
    if (!window.originalURL.hash || window.originalURL.hash.indexOf('#/') !== 0) return;
    console.log('redirecting...', window.originalURL.hash);
    let title = window.decodeURIComponent(window.originalURL.hash.substring(2));
    title = title.replace(/\W+/g, '_');
    let item = this.getItemWithCond(i => {
      return i.path.indexOf(title) === i.path.length - title.length;
    });
    if (item) {
      this.setActiveItem(item);
      return true;
    }
  }

  setMeta(item=null) {
    let curRoute = this.routes.getCurrentRoute();
    let title = curRoute.meta.title;
    let description = curRoute.meta.description;
    let keywords = curRoute.meta.keywords || [];
    if (item) {
      let meta = item.meta || {};
      title = (meta.title || item.title) + ' - ' + title;
      description = meta.description || item.summary || description;
      keywords = keywords.concat(item.keywords || []);
    }
    this.title.setTitle(title);
    this.meta.updateTag({name: 'title', content: title});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({name: 'keywords', content: keywords.join(',')});
  }

  setNavigation(nav) {
    let curRoute = this.routes.getCurrentRoute();
    this.onlyAPICalls = curRoute.ui === 'console';
    if (curRoute.openapi
        && (curRoute.openapi !== this.openapi.currentName || !this.openapi.parsed)) {
      return;
    }
    let needsInit = true;
    if (this.currentNavigationSettings
        && this.currentNavigationSettings.navigation === nav
        && this.currentNavigationSettings.openapi === this.openapi.parsed) {
      needsInit = false;
    }
    if (needsInit) {
      this.clearNavigation();
      this.items = initializeNavigation(nav, window.config, this.openapi.parsed) || [];
      this.routes.setCurrentNavigation(this.items);
      this.search.setItems(this.items);
      this.setTags();
    }
    this.currentNavigationSettings = {
      navigation: nav,
      openapi: this.openapi.parsed,
    };
    this.hideInvalidItems();
  }

  setTags() {
    this.tags = [];
    this.addTags(this.items);
    this.tags.sort();
  }

  addTags(navItems) {
    if (!navItems) return;
    navItems.filter(i => i.tags && i.tags !== true).forEach(item => {
      let tags = item.tags.filter(t => this.tags.indexOf(t) === -1);
      this.tags = this.tags.concat(tags);
      this.addTags(item.children);
    });
  }

  clearNavigation() {
    this.items = [];
    this.currentNavigationSettings = null;
    this.clearSelection();
  }

  setActiveItem(item) {
    if (!item) return this.clearSelection();
    if (item.autoselect && item.children) return this.setActiveItem(item.children[0]);
    let curPath = this.getPathFromParams(this.routes.getPathParams());
    if (item.path !== curPath) {
      if (item.path !== this.redirecting) {
        this.redirecting = item.path;
        this.routes.navigate(this.routes.getNavigationLink(item.path), true);
      }
    } else {
      this.redirecting = null;
      item.active = item.expanded = true;
      this.currentChoice = item;
      this.choose.emit(item);
    }
  }

  expandForParams(params) {
    if (!this.items || !this.items.length) {
      return;
    }
    this.collapseAll();
    if (params['apiCall'] || params['operation']) {
      let menuItem = this.getItemByType('operation', params['apiCall'] || params['operation']);
      return this.setActiveItem(menuItem);
    } else if (params['title']) {
      let menuItem = this.getItemByType('title', params['title']);
      return this.setActiveItem(menuItem);
    } else if (params['definition']) {
      let menuItem = this.getItemByType('definition', params['definition']);
      return this.setActiveItem(menuItem);
    }
    let path = this.getPathFromParams(params);
    let chosen = this.expandForPath(path);
    let curRoute = this.routes.getCurrentRoute();
    let defaultRoute = this.routes.getDefaultRoute();
    if (!chosen) {
      if (defaultRoute && path !== "/") {
        this.routes.navigate([defaultRoute]);
      } else if (curRoute.ui === 'documentation') {
        chosen = chosen || this.items[0];
      }
    }
    this.setActiveItem(chosen);
  }

  getPathFromParams(params) {
    let pathParts = [];
    let pathStart = 0;
    while (params['item' + pathStart]) {
      pathParts.push(params['item' + pathStart++]);
    }
    return '/' + pathParts.join('/');
  }

  expandForPath(path) {
    let chosen = this.expandFirstMatchingItem(path, this.items);
    return chosen;
  }

  getItemWithCond(cond, items=null) {
    if (!items) items = this.items;
    for (let i = 0; i < items.length; ++i) {
      if (cond(items[i])) return items[i];
      let child = this.getItemWithCond(cond, items[i].children || []);
      if (child) return child;
    }
    return null;
  }

  getItem(path) {
    return this.getItemWithCond(i => i.path === path);
  }

  getItemByType(type, value) {
    return this.getItemWithCond(i => i[type] === value);
  }

  clearSelection() {
    this.currentChoice = null;
    this.collapseAll();
    this.choose.emit(null);
  }

  collapseAll() {
    this.items.forEach(i => this.collapseItem(i));
  }

  collapseItem(item) {
    item.expanded = item.active = false;
    if (item.children) item.children.forEach(c => this.collapseItem(c));
  }

  expandFirstMatchingItem(path, items=null) {
    return this.expandWithCondition(i => i.path === path, items);
  }

  expandWithCondition(cond, items=null) {
    if (!items) items = this.items;
    for (let i = 0; i < items.length; ++i) {
      let item = items[i];
      if (cond(item) && !item.hidden) {
        item.expanded = true;
        return item;
      }
      let matchedChild = this.expandWithCondition(cond, item.children || []);
      if (matchedChild) {
        item.expanded = true;
        return matchedChild;
      }
    }
  }

  hideWithCondition(cond, items=null) {
    if (!items) items = this.items;
    items.forEach(item => {
      if (item.children) this.hideWithCondition(cond, item.children);
      let visibleChildren = (item.children || []).filter(c => !c.hidden);
      return item.hidden = (cond(item) && !visibleChildren.length) || item.hide;
    })
  }

  hideInvalidItems() {
    this.hideWithCondition(i => {
      if (i.hide) return true;
      if (this.onlyAPICalls && !i.operation) return true;
      if (this.openapi.parsed) {
        if (i.operation && !this.openapi.getAPICall(i.operation)) return true;
        if (i.definition && !this.openapi.parsed.definitions[i.definition]) return true;
      }
      return false;
    });
  }
}

@Pipe({
    name: 'getMenuItemFromSearch',
})
export class GetMenuItemFromSearchPipe implements PipeTransform {
  constructor(private menu:MenuService) {}

  transform(results: any[], args: any[]): any {
    if (!results) return [];
    let items = results
          .map(r => this.menu.getItem(r.ref) || {hidden: true})
          .filter(i => !i.hidden || i.searchable);
    if (!items.length) return [-1];
    return items;
  }
}

@Pipe({
    name: 'mapSearchResults',
})
export class MapSearchResultsPipe implements PipeTransform {
  constructor(private search:SearchService) {}

  transform(results: any[], args: any[]): any {
    let items = args[0];
    let tag = args[1];
    if (!this.search.query && !tag) return items;
    results = (results || []).map(r => r.ref);
    let matchesQuery = item => {
      if (item.hidden && !item.searchable) return false;
      let matchesSearch = !this.search.query || results.indexOf(item.path) !== -1;
      let matchesTag = !tag || (item.tags || []).indexOf(tag) !== -1;
      return (matchesSearch && matchesTag) || !!(item.children || []).filter(i => matchesQuery(i)).length;
    }
    let matches = items.filter(item => matchesQuery(item));
    return matches.length ? matches : [-1];
  }
}





// WEBPACK FOOTER //
// ./src/app/services/menu.ts